import React from 'react';

import { styled } from 'styled-components';

import theme from 'styles/theme';

import ContentContainer from 'components/ContentContainer/ContentContainer';
import CtaButton from 'components/CtaButton/CtaButton';
import { UnstyledInnerHtml } from 'components/InnerHtml/InnerHtml';
import Modal from 'components/Modal/Modal';

import useWindowDimensions from 'hooks/useWindowDimensions';

import { FullBleedCtaJumbotron } from 'interfaces/content-stack/modular-blocks/fullBleedCta';

import { autoOptimizeImage } from 'utils/functions';
import { getModularBlockLink } from 'utils/modularBlockUtils';

const FullBleedCtaJumbotronContainer = styled.div<{ $backgroundGradientWithImage?: boolean; $showGradient?: boolean }>`
  background-color: ${(props) => props.theme.colors.black};
  display: flex;
  align-items: center;
  height: 100vh;
  background-image: none;
  background-repeat: repeat;
  background-size: auto;
  background-attachment: scroll;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: ${(props) => props.theme.colors.white};
  position: relative;

  ${(props) =>
    props.$backgroundGradientWithImage &&
    `
    height: auto;
    color: ${props.theme.colors.black};
    padding-top: 2rem;
  `};

  ${(props) =>
    props.$showGradient &&
    `
    background: linear-gradient(0deg, rgba(202, 202, 202, 0.75), rgb(255, 255, 255) 54%);
  `};
`;

const CtaContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-around;
`;

const CtaTextWrapper = styled.div<{ $backgroundGradientWithImage?: boolean }>`
  gap: 1rem;
  display: flex;
  max-width: 65rem;
  flex-direction: column;
  align-items: center;
  position: relative;

  ${(props) =>
    !props.$backgroundGradientWithImage &&
    `
    @media (max-width: 1920px) {
      max-width: 54rem;
    }

    @media (max-width: 1440px) {
      max-width: 47rem;
    }

    @media (max-width: 991px) {
      max-width: none;
    }
  `};
`;

const HeadingWrapper = styled.div`
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 1rem;
  display: flex;
  text-align: center;
  align-items: center;
`;

const Title = styled.div<{ $backgroundGradientWithImage?: boolean }>`
  * {
    font-family: ${(props) => props.theme.fonts.fontFamilySecondary};
    font-size: 4rem;
    text-transform: uppercase;
    font-weight: 500;
    line-height: 1.6;

    @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
      font-size: 2.5rem;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.xs}) {
      font-size: 2.25rem;
    }

    ${(props) =>
      props.$backgroundGradientWithImage &&
      `
      p {
        margin-bottom: 0;
      }
    `};
  }
`;

const CtaText = styled.div<{ $backgroundGradientWithImage?: boolean }>`
  margin-top: 0.5rem;
  font-weight: 400;
  font-family: 'Worksans Variablefont Wght', sans-serif;
  font-size: 1.25rem;

  a {
    color: inherit;

    &:hover {
      color: ${(props) => props.theme.colors.primaryRed};
    }
  }

  @media (max-width: 479px) {
    font-size: 1rem;
  }

  ${(props) =>
    props.$backgroundGradientWithImage &&
    `
    p {
      margin-bottom: 0;
    }
`};
`;

const CtaOverlay = styled.div`
  mix-blend-mode: multiply;
  background-image: radial-gradient(circle, rgba(29, 25, 27, 0.95) 29%, rgba(27, 27, 27, 0) 94%);
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: pink;
`;

const BackgroundMediaContainer = styled.div`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;

  video,
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    background-position: 50%;
    background-size: cover;
    margin: auto;
    position: absolute;
    top: -100%;
    bottom: -100%;
    left: -100%;
    right: -100%;
  }
`;

const BgGradientImg = styled.img`
  width: 100%;
`;

const JumbotronFullBleedCta = (jumbotron: FullBleedCtaJumbotron) => {
  const { buttonCta, backgroundImageOrVideo, ctaHeader, ctaDescription, disclaimerModal, backgroundGradientWithImage } =
    jumbotron;
  const { width } = useWindowDimensions();
  const isMobile = width <= theme.breakpoints.values.lg;
  const ctaHref = getModularBlockLink(buttonCta.link);
  const hasBgGradientWithImg = !!backgroundGradientWithImage?.image;

  const media = backgroundImageOrVideo && {
    type: backgroundImageOrVideo.featureImageDesktop ? 'image' : backgroundImageOrVideo.featureVideo && 'video',
    desktop: backgroundImageOrVideo.featureImageDesktop?.url ?? backgroundImageOrVideo.featureVideo?.url,
    mobile:
      backgroundImageOrVideo.featureImageMobile?.url ??
      backgroundImageOrVideo.featureImageDesktop?.url ??
      backgroundImageOrVideo.featureVideo?.url,
  };

  return (
    <FullBleedCtaJumbotronContainer
      $backgroundGradientWithImage={hasBgGradientWithImg}
      $showGradient={backgroundGradientWithImage.showGradient}
    >
      {media && (
        <BackgroundMediaContainer>
          {media.type === 'image' && (
            <img src={autoOptimizeImage(isMobile ? media.mobile : media.desktop)} alt='' loading='lazy' />
          )}
          {media.type === 'video' && (
            <video autoPlay loop muted playsInline>
              <source src={media.desktop} />
            </video>
          )}
        </BackgroundMediaContainer>
      )}

      <ContentContainer innerPaddingOverride='0'>
        <CtaContainer>
          <CtaTextWrapper $backgroundGradientWithImage={hasBgGradientWithImg}>
            <HeadingWrapper>
              <Title $backgroundGradientWithImage={hasBgGradientWithImg}>
                <UnstyledInnerHtml richText={ctaHeader} />
              </Title>
              <CtaText $backgroundGradientWithImage={hasBgGradientWithImg}>
                <UnstyledInnerHtml richText={ctaDescription} />
              </CtaText>
            </HeadingWrapper>
            <CtaButton text={buttonCta.buttonText} to={ctaHref} banner dataLayerInfo={ctaHeader} />
            {disclaimerModal.modalTrigger && (
              <Modal
                className='modal-trigger-cta-disclaimer'
                header={disclaimerModal.modalContent.modalHeader}
                body={<UnstyledInnerHtml richText={disclaimerModal.modalContent.modalBody} />}
                trigger={disclaimerModal.modalTrigger}
                triggerMargin='0 0 3rem'
                showInfoIcon
              />
            )}
          </CtaTextWrapper>
        </CtaContainer>
        {hasBgGradientWithImg && (
          <BgGradientImg
            src={autoOptimizeImage(backgroundGradientWithImage.image?.url, true, '1340')}
            alt={backgroundGradientWithImage.altImageText}
            loading='lazy'
          />
        )}
      </ContentContainer>
      {!hasBgGradientWithImg && <CtaOverlay />}
    </FullBleedCtaJumbotronContainer>
  );
};

export default JumbotronFullBleedCta;
